import React, { useEffect, useRef, useState } from "react";

interface Location {
  latitude: number;
  longitude: number;
}

interface GooglePlacesAutocompleteProps {
  placeholder?: string;
  onSelectLocation: (location: Location, address: string) => void;
  initialValue?: string;
  className?: string;
}

const GOOGLE_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "YOUR_DEFAULT_KEY_HERE"; // Replace if needed

declare global {
  interface Window {
    google: any;
    initGooglePlaces?: () => void;
  }
}

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  placeholder = "Search for a location",
  onSelectLocation,
  initialValue = "",
  className = "",
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState(initialValue || "");

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      if (window.google?.maps?.places?.Autocomplete) {
        setIsLoaded(true);
        return Promise.resolve();
      }

      return new Promise<void>((resolve, reject) => {
        const scriptId = "google-maps-script";
        const existingScript = document.getElementById(scriptId);

        if (existingScript) {
          const checkLoaded = () => {
            if (window.google?.maps?.places?.Autocomplete) {
              setIsLoaded(true);
              resolve();
            } else {
              setTimeout(checkLoaded, 100);
            }
          };
          checkLoaded();
          return;
        }

        window.initGooglePlaces = () => {
          if (window.google?.maps?.places?.Autocomplete) {
            setIsLoaded(true);
            resolve();
          } else {
            const error = new Error("Google Places API not available");
            setError(error.message);
            reject(error);
          }
        };

        const script = document.createElement("script");
        script.id = scriptId;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&callback=initGooglePlaces`;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
          const error = new Error("Failed to load Google Maps script");
          setError(error.message);
          reject(error);
        };
        document.head.appendChild(script);
      });
    };

    loadGoogleMapsScript().catch((err) => {
      setError(err.message);
    });

    return () => {
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current
        );
      }
    };
  }, []);

  useEffect(() => {
    if (isLoaded && inputRef.current) {
      initializeAutocomplete();
    }
  }, [isLoaded]);

  const initializeAutocomplete = () => {
    if (!inputRef.current || !window.google?.maps?.places?.Autocomplete) {
      return;
    }

    try {
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current
        );
      }

      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ["geocode"],
          componentRestrictions: { country: "in" },
          fields: ["geometry", "formatted_address", "name"],
        }
      );

      autocompleteRef.current = autocomplete;

      autocomplete.addListener("place_changed", () => {
        try {
          const place = autocomplete.getPlace();
          if (!place || !place.geometry || !place.geometry.location) {
            return;
          }

          const lat =
            typeof place.geometry.location.lat === "function"
              ? place.geometry.location.lat()
              : place.geometry.location.lat;
          const lng =
            typeof place.geometry.location.lng === "function"
              ? place.geometry.location.lng()
              : place.geometry.location.lng;

          const address = place.formatted_address || place.name || "";

          if (!isNaN(lat) && !isNaN(lng)) {
            onSelectLocation({ latitude: lat, longitude: lng }, address);
          } else {
            console.error("Invalid coordinates:", { lat, lng });
          }
        } catch (error) {
          console.error("Error in place_changed:", error);
        }
      });
    } catch (err) {
      setError("Failed to initialize autocomplete");
    }
  };

  // Error UI
  if (error) {
    return (
      <div
        className={`w-full border border-red-300 p-2 rounded-md bg-red-50 text-red-600 ${className}`}
      >
        <div className="text-sm">Error: {error}</div>
        <button
          onClick={() => {
            setError(null);
            window.location.reload();
          }}
          className="mt-1 text-xs underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  // Loading UI
  if (!isLoaded) {
    return (
      <div
        className={`w-full border p-2 rounded-md bg-gray-50 text-gray-500 ${className}`}
      >
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
          Loading Google Places...
        </div>
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={inputValue}
      onChange={(e) => {
        setInputValue(e.target.value);
        if (error) setError(null);
      }}
      placeholder={placeholder}
      className={`w-full rounded-lg border border-gray-300 px-4 py-2 text-sm shadow-sm transition focus-within:ring-1 focus:border-stroke focus:ring-1 focus:outline-none dark:bg-form-input dark:text-white dark:border-form-strokedark ${className}`}
    />
  );
};

export default GooglePlacesAutocomplete;
